/**
 * Contact Us page - submit inquiries to the InDeep team.
 * 
 * MVP Contact Us form implementation.
 * 
 * SAFETY: No personal identity required. Public endpoint.
 */

import { useState } from 'react';

/**
 * Contact form state.
 */
type FormState = 'idle' | 'submitting' | 'success' | 'error';

/**
 * Email validation regex.
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Message constraints.
 */
const MESSAGE_MIN_LENGTH = 10;
const MESSAGE_MAX_LENGTH = 10000;

/**
 * Success state component.
 */
function SuccessState() {
  return (
    <div className="success-state" data-testid="contact-success">
      <div className="success-icon">✓</div>
      <h2 className="success-title">Message Sent</h2>
      <p className="success-message">
        Thank you for your message. We will get back to you soon.
      </p>
    </div>
  );
}

/**
 * Error state component.
 */
function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="error-state" data-testid="contact-error">
      <div className="error-icon">!</div>
      <h2 className="error-title">Submission Failed</h2>
      <p className="error-message">{message}</p>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={onRetry}
        data-testid="retry-button"
      >
        Try Again
      </button>
    </div>
  );
}

/**
 * Contact page component.
 */
export function ContactPage() {
  const [formState, setFormState] = useState<FormState>('idle');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [messageError, setMessageError] = useState('');
  const [serverError, setServerError] = useState('');

  /**
   * Validates the email field.
   */
  const validateEmail = (value: string): boolean => {
    if (!value.trim()) {
      setEmailError('Email is required');
      return false;
    }
    if (!EMAIL_REGEX.test(value)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  /**
   * Validates the message field.
   */
  const validateMessage = (value: string): boolean => {
    if (!value.trim()) {
      setMessageError('Message is required');
      return false;
    }
    if (value.trim().length < MESSAGE_MIN_LENGTH) {
      setMessageError('Message must be at least 10 characters');
      return false;
    }
    if (value.length > MESSAGE_MAX_LENGTH) {
      setMessageError('Message is too long');
      return false;
    }
    setMessageError('');
    return true;
  };

  /**
   * Handles email change with validation.
   */
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError) {
      validateEmail(value);
    }
  };

  /**
   * Handles message change with validation.
   */
  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setMessage(value);
    if (messageError) {
      validateMessage(value);
    }
  };

  /**
   * Handles form submission.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields
    const isEmailValid = validateEmail(email);
    const isMessageValid = validateMessage(message);

    if (!isEmailValid || !isMessageValid) {
      return;
    }

    setFormState('submitting');
    setServerError('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          message: message.trim(),
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send message');
      }

      setFormState('success');
    } catch (err) {
      setFormState('error');
      setServerError(err instanceof Error ? err.message : 'Failed to send message');
    }
  };

  /**
   * Handles retry after error.
   */
  const handleRetry = () => {
    setFormState('idle');
    setServerError('');
  };

  // Success state
  if (formState === 'success') {
    return (
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">Contact Us</h1>
        </div>
        <SuccessState />
        <style>{contactPageStyles}</style>
      </div>
    );
  }

  // Error state
  if (formState === 'error') {
    return (
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">Contact Us</h1>
        </div>
        <ErrorState message={serverError} onRetry={handleRetry} />
        <style>{contactPageStyles}</style>
      </div>
    );
  }

  // Form state
  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Contact Us</h1>
        <p className="page-description">
          Have a question or feedback? Send us a message and we will get back to you.
        </p>
      </div>

      <div className="contact-content" data-testid="contact-form-container">
        <form onSubmit={handleSubmit} className="contact-form" data-testid="contact-form">
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className={`form-input ${emailError ? 'form-input-error' : ''}`}
              value={email}
              onChange={handleEmailChange}
              placeholder="your@email.com"
              disabled={formState === 'submitting'}
              data-testid="email-input"
            />
            {emailError && (
              <span className="form-error" data-testid="email-error">
                {emailError}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="message" className="form-label">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              className={`form-textarea ${messageError ? 'form-input-error' : ''}`}
              value={message}
              onChange={handleMessageChange}
              placeholder="How can we help you?"
              rows={5}
              maxLength={MESSAGE_MAX_LENGTH}
              disabled={formState === 'submitting'}
              data-testid="message-input"
            />
            {messageError && (
              <span className="form-error" data-testid="message-error">
                {messageError}
              </span>
            )}
            <span className="form-hint" data-testid="message-hint">
              {message.length}/{MESSAGE_MAX_LENGTH} characters
            </span>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={formState === 'submitting'}
            data-testid="submit-button"
          >
            {formState === 'submitting' ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      </div>

      <style>{contactPageStyles}</style>
    </div>
  );
}

/**
 * Contact page styles.
 */
const contactPageStyles = `
  .contact-content {
    margin-top: var(--spacing);
  }
  .contact-form {
    max-width: 480px;
  }
  .form-group {
    margin-bottom: var(--spacing);
  }
  .form-label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: var(--color-text);
  }
  .form-input,
  .form-textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius);
    font-size: 1rem;
    background: var(--color-surface);
    color: var(--color-text);
    box-sizing: border-box;
  }
  .form-input:focus,
  .form-textarea:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
  .form-input:disabled,
  .form-textarea:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .form-input-error {
    border-color: var(--color-error);
  }
  .form-error {
    display: block;
    margin-top: 4px;
    font-size: 0.85rem;
    color: var(--color-error);
  }
  .form-hint {
    display: block;
    margin-top: 4px;
    font-size: 0.8rem;
    color: var(--color-text-muted);
    text-align: right;
  }
  .btn {
    padding: 12px 24px;
    border-radius: var(--radius);
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    border: none;
  }
  .btn-primary {
    background: var(--color-accent);
    color: #3C3C3C;
  }
  .btn-primary:hover:not(:disabled) {
    opacity: 0.9;
  }
  .btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .btn-secondary {
    background: var(--color-surface);
    color: var(--color-text);
    border: 1px solid var(--color-border);
  }
  .btn-secondary:hover {
    background: var(--color-surface-hover, #f5f5f5);
  }
  .success-state,
  .error-state {
    text-align: center;
    padding: var(--spacing-lg);
    margin-top: var(--spacing);
  }
  .success-icon,
  .error-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    font-size: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto var(--spacing);
  }
  .success-icon {
    background: var(--color-success-bg, #e6f4ea);
    color: var(--color-success, #1e7e34);
  }
  .error-icon {
    background: var(--color-error-bg, #ffebee);
    color: var(--color-error, #d32f2f);
  }
  .success-title,
  .error-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: var(--spacing);
  }
  .success-message,
  .error-message {
    color: var(--color-text-muted);
    margin-bottom: var(--spacing);
    line-height: 1.5;
  }
`;
